import { useRef, type KeyboardEvent } from 'react';
import { Ikon } from '../../status/Ikon.tsx';

export interface Valg<K extends string> {
  verdi: K;
  ord: string;
  ikon?: string;
  /** En telling (filteret), aldri en rangering (DD-38). */
  tall?: number;
}

/**
 * Radiogruppen (DD-56, DD-08) — rolle, tilgang per rom og filteret har samme form som skallets
 * rom-bryter: segmenter, aldri et nedtrekk, så valget alltid er synlig. Det valgte bærer
 * hakemerket i stedet for ikonet — form og flate, ikke farge alene.
 *
 * Tastatur (DD-11, wireframens tabell): ÉTT tabstopp per gruppe (roving tabindex); piltastene
 * flytter valget OG fokuset, Home og End til første og siste. En låst gruppe har
 * `aria-disabled` på hvert segment og grunnen i DOM via `aria-describedby` — aldri en stum
 * grå knapp (`ux-flow-architect`, «Forklarte låser»).
 */
export function Radiogruppe<K extends string>({
  navn,
  valg,
  aktiv,
  onVelg,
  laast = false,
  grunnId,
  fyll = false,
}: {
  navn: string;
  valg: ReadonlyArray<Valg<K>>;
  aktiv: K;
  onVelg: (verdi: K) => void;
  laast?: boolean;
  grunnId?: string;
  /** Segmentene deler bredden likt (personsiden). Filteret tar bredden av ordene. */
  fyll?: boolean;
}) {
  const rot = useRef<HTMLDivElement>(null);

  const flytt = (i: number) => {
    const neste = valg[(i + valg.length) % valg.length];
    if (!neste) return;
    onVelg(neste.verdi);
    rot.current?.querySelectorAll<HTMLButtonElement>('[role="radio"]')[(i + valg.length) % valg.length]?.focus();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (laast) return;
    const i = valg.findIndex((v) => v.verdi === aktiv);
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      flytt(i + 1);
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      flytt(i - 1);
    } else if (e.key === 'Home') {
      e.preventDefault();
      flytt(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      flytt(valg.length - 1);
    }
  };

  return (
    <div ref={rot} role="radiogroup" aria-label={navn} aria-describedby={laast ? grunnId : undefined} onKeyDown={onKeyDown} className={fyll ? 'panel-pb-seg panel-pb-seg-fyll' : 'panel-pb-seg'}>
      {valg.map((v) => {
        const paa = v.verdi === aktiv;
        return (
          <button
            key={v.verdi}
            type="button"
            role="radio"
            aria-checked={paa}
            aria-disabled={laast || undefined}
            tabIndex={paa ? 0 : -1}
            className="panel-pb-seg-valg"
            onClick={laast ? undefined : () => onVelg(v.verdi)}
          >
            {paa ? <Ikon navn="checkmark-outline" className="panel-pb-hake" /> : v.ikon ? <Ikon navn={v.ikon} /> : null}
            {v.ord}
            {v.tall !== undefined ? <span className="panel-pb-seg-tall">{v.tall}</span> : null}
          </button>
        );
      })}
    </div>
  );
}
